'use client';

import React, { Suspense, ReactNode } from 'react';
import { useThemeInitializer } from '@/hooks/useThemeInitializer';
import { TemplateWrapper } from '@/templates';
import { config, getTemplateAssetPath } from '@/config';

/**
 * Loading component for template initialization
 */
const TemplateLoadingFallback: React.FC<{ templateName: string }> = ({
  templateName,
}) => (
  <div className="flex min-h-screen items-center justify-center bg-gray-50">
    <div className="text-center">
      {/* <img
        src={getTemplateAssetPath('loader')}
        alt="loading"
        className="h-12 w-12"
        onError={(e) => {
          // Fallback to default loader if corporate loader fails
          e.currentTarget.src = `/${config.corporateName}/loader.gif`;
        }}
      /> */}
      Loading...
    </div>
  </div>
);

/**
 * Error component for template loading failures
 */
const TemplateErrorFallback: React.FC<{
  error: string;
  templateName: string;
}> = ({ error, templateName }) => (
  <div className="flex min-h-screen items-center justify-center bg-red-50">
    <div className="max-w-md rounded-lg bg-white p-6 text-center shadow-lg">
      <h2 className="mb-2 text-xl font-semibold text-red-600">
        Template Error
      </h2>
      <p className="mb-4 text-red-500">
        Failed to load {templateName} template
      </p>
      <p className="text-sm text-gray-600">{error}</p>
    </div>
  </div>
);

/**
 * Props for TemplateLoader
 */
interface TemplateLoaderProps {
  children?: ReactNode;
  fallback?: ReactNode;
  errorFallback?: ReactNode;
}

/**
 * Dynamic Template Loader Component
 *
 * This component ensures:
 * 1. Theme is initialized before template rendering
 * 2. Template is loaded dynamically based on config
 * 3. Proper error handling and loading states
 * 4. TypeScript-safe template loading
 */
export const TemplateLoader: React.FC<TemplateLoaderProps> = ({
  children,
  fallback,
  errorFallback,
}) => {
  const { isInitialized, themeName, error } = useThemeInitializer();

  // Show error state
  if (error) {
    return (
      errorFallback || (
        <TemplateErrorFallback
          error={error}
          templateName={config.templateConfig || 'default'}
        />
      )
    );
  }

  // Show loading state while theme initializes
  if (!isInitialized) {
    return (
      fallback || (
        <TemplateLoadingFallback
          templateName={config.templateConfig || 'default'}
        />
      )
    );
  }

  // Theme is ready, render template with suspense
  return (
    <Suspense
      fallback={
        fallback || (
          <TemplateLoadingFallback
            templateName={config.templateConfig || 'default'}
          />
        )
      }
    >
      <TemplateWrapper>{children}</TemplateWrapper>
    </Suspense>
  );
};

/**
 * Higher-order component for template loading
 */
export const withTemplateLoader = <P extends object>(
  Component: React.ComponentType<P>,
  options?: {
    fallback?: ReactNode;
    errorFallback?: ReactNode;
  },
) => {
  return function LoadedTemplateComponent(props: P) {
    return (
      <TemplateLoader
        fallback={options?.fallback}
        errorFallback={options?.errorFallback}
      >
        <Component {...props} />
      </TemplateLoader>
    );
  };
};

/**
 * Hook for accessing template loading state
 */
export const useTemplateLoader = () => {
  const themeState = useThemeInitializer();

  return {
    isTemplateReady: themeState.isInitialized,
    themeName: themeState.themeName,
    error: themeState.error,
    isLoading: !themeState.isInitialized && !themeState.error,
  };
};

export default TemplateLoader;
